// The parameter drawer (spec/dashboard.md section 5.3): every field of a parameter with the type's own fields, the
// default through the type's widget, and the save rules — a rename or a type change dry-runs first and shows the impact
// (R-CM-04, `dropValues` offered when the API answers 409), other changes save directly; a regex is checked on blur and
// an invalid one is an inline error, never a throw (Q-D-1).
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { Json, Parameter, SchemaChangeImpact, Template } from '../../api/types';
import { formatValue } from '../../content/format';
import { displayNameFrom, LIST_ELEMENT_TYPES, PARAM_TYPES, PARAMETER_FLAGS, parameterNameError, regexError, targetKinds, TYPE_LABEL, UNIQUE_TYPES, type ParamType, ASSET_KINDS } from '../../content/schema';
import { FieldWidget } from '../../content/widgets/FieldWidget';
import { isComponentTarget } from '../../content/widgets/context';
import { isSystemTemplate, routes } from '../../shell/routes';
import { Button } from '../../ui/Button';
import { Combobox } from '../../ui/Combobox';
import { Field } from '../../ui/Field';
import { AlertTriangle } from '../../ui/icons';
import { Checkbox, Select, TextArea, TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { fieldErrors, FlagChips, parameterRequest, useSchemaEnv } from './common';

/** The asset kinds of the API (`AssetKinds.All` on the server); the foundation has no shared list of them. */

/** The type-specific part of a parameter: also the whole of a list's element type. */
interface Typed {
  type: ParamType;
  template: string | null;
  enum: string | null;
  group: string | null;
  assetKinds: string[];
  regex: string;
}

interface ParamDraft extends Typed {
  name: string;
  displayName: string;
  displayNameTouched: boolean;
  description: string;
  of: Typed | null;
  default: Json | null;
  isRequired: boolean;
  isUnique: boolean;
  usedInName: boolean;
  flags: string[];
}

const emptyTyped = (type: ParamType): Typed => ({ type, template: null, enum: null, group: null, assetKinds: [], regex: '' });

function typedOf(p: Pick<Parameter, 'type' | 'template' | 'enum' | 'group' | 'assetKinds' | 'regex'>): Typed {
  return { type: p.type, template: p.template ?? null, enum: p.enum ?? null, group: p.group ?? null, assetKinds: [...(p.assetKinds ?? [])], regex: p.regex ?? '' };
}

function draftOf(p: Parameter | null): ParamDraft {
  if (!p) return { ...emptyTyped('string'), name: '', displayName: '', displayNameTouched: false, description: '', of: null, default: null, isRequired: false, isUnique: false, usedInName: false, flags: [] };
  return {
    ...typedOf(p),
    name: p.name,
    displayName: p.displayName ?? '',
    displayNameTouched: true,
    description: p.description ?? '',
    of: p.of ? typedOf(p.of) : null,
    default: (p.default ?? null) as Json | null,
    isRequired: p.isRequired,
    isUnique: p.isUnique,
    usedInName: p.usedInName,
    flags: [...(p.flags ?? [])],
  };
}

function typedParameter(t: Typed, name: string, declaredBy: string | null): Parameter {
  return {
    id: '',
    unnyId: null,
    name,
    displayName: null,
    description: null,
    type: t.type,
    of: null,
    template: t.type === 'document' || t.type === 'object' || t.type === 'profile_data' ? t.template : null,
    enum: t.type === 'enum' ? t.enum : null,
    group: t.type === 'localized_string' ? t.group : null,
    assetKinds: t.type === 'asset' ? t.assetKinds : [],
    regex: t.type === 'string' && t.regex ? t.regex : null,
    default: null,
    isUnique: false,
    isRequired: false,
    usedInName: false,
    flags: [],
    legacyFlags: 0,
    declaredBy,
  };
}

/** The draft as a parameter (the default widget, the type label, the create-state table). */
export function parameterOf(d: ParamDraft, declaredBy: string | null, base?: Parameter | null): Parameter {
  const own = typedParameter(d, d.name, declaredBy);
  return {
    ...own,
    id: base?.id ?? '',
    unnyId: base?.unnyId ?? null,
    displayName: d.displayName || null,
    description: d.description || null,
    of: d.type === 'list' && d.of ? typedParameter(d.of, '', declaredBy) : null,
    default: d.default,
    isUnique: UNIQUE_TYPES.has(d.type) ? d.isUnique : false,
    isRequired: d.isRequired,
    usedInName: d.usedInName,
    flags: d.flags,
    legacyFlags: base?.legacyFlags ?? 0,
  };
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** The PATCH body of the fields that changed ('' clears a string field on the server; `clearDefault` clears the default). */
function changedFields(before: Parameter, after: Parameter): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (after.name !== before.name) body.name = after.name;
  if ((after.displayName ?? '') !== (before.displayName ?? '')) body.displayName = after.displayName ?? '';
  if ((after.description ?? '') !== (before.description ?? '')) body.description = after.description ?? '';
  if (after.type !== before.type) body.type = after.type;
  if (after.type === 'list' && !same(after.of && parameterRequest(after.of), before.of && parameterRequest(before.of))) body.of = after.of ? parameterRequest(after.of) : null;
  for (const key of ['template', 'enum', 'group', 'regex'] as const) {
    if ((after[key] ?? '') !== (before[key] ?? '')) body[key] = after[key] ?? '';
  }
  if (!same(after.assetKinds, before.assetKinds)) body.assetKinds = after.assetKinds;
  if (!same(after.default, before.default)) {
    if (after.default === null || after.default === undefined) body.clearDefault = true;
    else body.default = after.default;
  }
  if (after.isUnique !== before.isUnique) body.isUnique = after.isUnique;
  if (after.isRequired !== before.isRequired) body.isRequired = after.isRequired;
  if (after.usedInName !== before.usedInName) body.usedInName = after.usedInName;
  if (!same(after.flags, before.flags)) body.flags = after.flags;
  return body;
}

export interface ParameterDrawerProps {
  /** the template on the server; null while the template is being created (the drawer then edits the local list) */
  template: Template | null;
  templateName: string;
  /** the parameter edited; null for "+ Add parameter" */
  parameter: Parameter | null;
  /** names already taken in the family (the duplicate check) */
  takenNames: string[];
  /** the documents of the family (the delete confirm) */
  familyDocuments: number;
  /** the create state: the parameter goes into the template's POST instead of its own call */
  local?: { onSave: (p: Parameter, previousName: string | null) => void; onDelete: (name: string) => void };
  onClose: () => void;
}

export function ParameterDrawer({ template, templateName, parameter, takenNames, familyDocuments, local, onClose }: ParameterDrawerProps) {
  const env = useSchemaEnv();
  const { client, path, catalog, confirm, toasts, afterWrite, fail, canWrite } = env;
  const [draft, setDraft] = useState<ParamDraft>(() => draftOf(parameter));
  const [regexErr, setRegexErr] = useState<string | null>(null);
  const [ofRegexErr, setOfRegexErr] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [impact, setImpact] = useState<{ body: Record<string, unknown>; data: SchemaChangeImpact | null; conflict: string | null } | null>(null);

  const set = (patch: Partial<ParamDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const nameError = parameterNameError(draft.name) ?? (draft.name !== parameter?.name && takenNames.includes(draft.name) ? `A parameter named ${draft.name} already exists in the family of ${templateName}.` : null);
  const asParameter = useMemo(() => parameterOf(draft, templateName, parameter), [draft, templateName, parameter]);
  const dirty = !parameter || Object.keys(changedFields(parameter, asParameter)).length > 0;
  const invalid = !!nameError || !!regexErr || !!ofRegexErr || (draft.type === 'list' && !draft.of) || (draft.type === 'enum' && !draft.enum) || (draft.type === 'list' && draft.of?.type === 'enum' && !draft.of.enum);

  const changeType = (type: ParamType) =>
    setDraft((d) => ({
      ...d,
      type,
      default: null,
      isUnique: UNIQUE_TYPES.has(type) ? d.isUnique : false,
      of: type === 'list' ? (d.of ?? emptyTyped('int')) : null,
      template: targetKinds(type).length > 0 ? d.template : null,
    }));

  const paramPath = parameter ? { ...path, template: templateName, param: parameter.name } : null;

  const apply = async (body: Record<string, unknown>, dropValues: boolean) => {
    if (!paramPath || !template) return;
    setBusy(true);
    try {
      const result = await client.call(ops.updateParameter, { path: paramPath, ifMatch: Number(template.version), body: { ...body, dropValues } });
      afterWrite(result.commit);
      toasts.success(`Parameter ${asParameter.name} saved.`);
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && body.type !== undefined) {
        setImpact((prev) => ({ body, data: prev?.data ?? null, conflict: e.message }));
      } else {
        setServerErrors(fieldErrors(e));
        fail(e, `Saving ${asParameter.name} failed`);
      }
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    const re = draft.type === 'string' ? regexError(draft.regex) : null;
    if (re) {
      setRegexErr(re);
      return;
    }
    if (invalid) return;
    setServerErrors({});
    if (local) {
      local.onSave(asParameter, parameter?.name ?? null);
      onClose();
      return;
    }
    if (!template) return;
    if (!parameter) {
      setBusy(true);
      try {
        const result = await client.call(ops.addParameter, { path: { ...path, template: templateName }, ifMatch: Number(template.version), body: parameterRequest(asParameter) });
        afterWrite(result.commit);
        toasts.success(`Parameter ${asParameter.name} added.`);
        onClose();
      } catch (e) {
        setServerErrors(fieldErrors(e));
        fail(e, `Adding ${asParameter.name} failed`);
      } finally {
        setBusy(false);
      }
      return;
    }
    const body = changedFields(parameter, asParameter);
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    if (body.name !== undefined || body.type !== undefined) {
      // a rename or a type change rewrites documents: the impact first (R-CM-04)
      setBusy(true);
      try {
        const result = await client.call(ops.updateParameter, { path: paramPath!, ifMatch: Number(template.version), query: { dryRun: 'true' }, body: body });
        setImpact({ body, data: result.data as unknown as SchemaChangeImpact, conflict: null });
      } catch (e) {
        if (e instanceof ApiError && e.status === 409 && body.type !== undefined) setImpact({ body, data: null, conflict: e.message });
        else {
          setServerErrors(fieldErrors(e));
          fail(e, `Checking the change of ${parameter.name} failed`);
        }
      } finally {
        setBusy(false);
      }
      return;
    }
    await apply(body, false);
  };

  const remove = async () => {
    if (!parameter) return;
    const ok = await confirm({
      title: `Delete parameter ${parameter.displayName ?? parameter.name}?`,
      body: local ? `${parameter.name} is removed from the new template.` : `The value of ${parameter.name} is removed from ${familyDocuments} document${familyDocuments === 1 ? '' : 's'} of the ${templateName} family. The deletion is a commit of its own.`,
      confirmLabel: 'Delete parameter',
      tone: 'danger',
    });
    if (!ok) return;
    if (local) {
      local.onDelete(parameter.name);
      onClose();
      return;
    }
    if (!template) return;
    setBusy(true);
    try {
      const result = await client.call(ops.deleteParameter, { path: paramPath!, ifMatch: Number(template.version) });
      afterWrite(result.commit);
      toasts.success(`Parameter ${parameter.name} deleted.`);
      onClose();
    } catch (e) {
      fail(e, `Deleting ${parameter.name} failed`);
    } finally {
      setBusy(false);
    }
  };

  const noDefault = draft.type === 'profile_data' || isComponentTarget(catalog, asParameter) || (draft.type === 'list' && !!asParameter.of && isComponentTarget(catalog, asParameter.of));
  const widgetKey = JSON.stringify([draft.type, draft.enum, draft.template, draft.of, draft.regex, draft.default === null]);

  return (
    <Modal
      drawer
      open
      title={parameter ? `Parameter ${parameter.name}` : 'New parameter'}
      onClose={onClose}
      footer={
        impact ? null : (
          <>
            {parameter && (
              <Button variant="danger" onClick={() => void remove()} disabled={!canWrite || busy}>
                Delete
              </Button>
            )}
            <span className="spacer" />
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={() => void save()} busy={busy} disabled={!canWrite || invalid || !dirty}>
              {parameter ? 'Save' : local ? 'Add' : 'Create'}
            </Button>
          </>
        )
      }
    >
      {impact ? (
        <section className="impact" aria-label="Impact of the change">
          <h3>Impact of the change</h3>
          {impact.data && (
            <div className="stat-strip impact-stats">
              <div className="stat">
                <span className="stat-label">Documents rewritten</span>
                <span className="stat-value">{Number(impact.data.documents)}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Documents of the family</span>
                <span className="stat-value">{Number(impact.data.references)}</span>
              </div>
            </div>
          )}
          {impact.data && impact.data.notes.length > 0 && (
            <ul className="impact-notes">
              {impact.data.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          )}
          {impact.conflict && (
            <p className="impact-conflict" role="alert">
              <AlertTriangle aria-hidden="true" />
              <span>{impact.conflict}</span>
            </p>
          )}
          <div className="row impact-actions">
            <Button onClick={() => setImpact(null)}>Cancel</Button>
            <span className="spacer" />
            {impact.conflict ? (
              <Button variant="danger" busy={busy} onClick={() => void apply(impact.body, true)}>
                Apply and drop values that cannot be converted
              </Button>
            ) : (
              <Button variant="primary" busy={busy} onClick={() => void apply(impact.body, false)}>
                Apply
              </Button>
            )}
          </div>
        </section>
      ) : (
        <div className="drawer-form">
          <section className="drawer-section">
            <div className="section-head">
              <h3>Identity</h3>
            </div>
            <div className="form-grid">
              <Field label="Name" required error={nameError ?? serverErrors.name} help="Letters, digits and underscores; the JSON key of the value.">
                <TextInput className="mono" value={draft.name} onChange={(e) => set({ name: e.target.value, ...(draft.displayNameTouched ? {} : { displayName: displayNameFrom(e.target.value) }) })} autoComplete="off" />
              </Field>
              <Field label="Display name" error={serverErrors.displayName}>
                <TextInput value={draft.displayName} onChange={(e) => set({ displayName: e.target.value, displayNameTouched: true })} />
              </Field>
              <Field className="span-2" label="Description">
                <TextArea rows={2} value={draft.description} onChange={(e) => set({ description: e.target.value })} />
              </Field>
            </div>
          </section>
          <section className="drawer-section">
            <div className="section-head">
              <h3>Type</h3>
            </div>
            <div className="form-grid">
              <Field label="Type" required error={serverErrors.type} help={PARAM_TYPES.find((t) => t.value === draft.type)?.hint}>
                <Select value={draft.type} onChange={(e) => changeType(e.target.value as ParamType)}>
                  {PARAM_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <TypeFields value={draft} onChange={(t) => set({ ...t, default: t.type !== draft.type ? null : draft.default })} regexErr={regexErr} setRegexErr={setRegexErr} errors={serverErrors} />
              {draft.type === 'list' && (
                <fieldset className="inline-group element-type span-2">
                  <legend className="inline-group-title">Element type</legend>
                  <Field label="Element type" required>
                    <Select value={draft.of?.type ?? 'int'} onChange={(e) => set({ of: emptyTyped(e.target.value as ParamType), default: null })}>
                      {LIST_ELEMENT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {TYPE_LABEL.get(t) ?? t}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  {draft.of && <TypeFields value={draft.of} onChange={(t) => set({ of: t, default: null })} regexErr={ofRegexErr} setRegexErr={setOfRegexErr} errors={{}} nested />}
                </fieldset>
              )}
              <Field className="span-2" label="Default" help={noDefault ? 'This type has no default value.' : draft.default === null ? 'No default: new documents get the type’s empty value.' : formatValue(asParameter, draft.default, catalog.format)}>
                {noDefault ? (
                  <span className="muted small">—</span>
                ) : (
                  <div className="default-widget">
                    <FieldWidget key={widgetKey} param={asParameter} value={draft.default ?? undefined} onCommit={(v) => set({ default: v })} disabled={!canWrite} />
                    <Button size="s" variant="ghost" onClick={() => set({ default: null })} disabled={draft.default === null}>
                      No default
                    </Button>
                  </div>
                )}
              </Field>
            </div>
          </section>
          <section className="drawer-section">
            <div className="section-head">
              <h3>Constraints and flags</h3>
            </div>
            <div className="check-row">
              <Checkbox label="Required" checked={draft.isRequired} onChange={(e) => set({ isRequired: e.target.checked })} />
              {UNIQUE_TYPES.has(draft.type) && <Checkbox label="Unique" checked={draft.isUnique} onChange={(e) => set({ isUnique: e.target.checked })} />}
              <Checkbox label="Used in name" checked={draft.usedInName} onChange={(e) => set({ usedInName: e.target.checked })} title="Feeds the auto display format" />
            </div>
            <Field label="Flags">
              <FlagChips label="Parameter flags" catalog={PARAMETER_FLAGS} value={draft.flags} onChange={(flags) => set({ flags })} />
            </Field>
          </section>
        </div>
      )}
    </Modal>
  );
}

/** The type's own fields (section 5.3); `nested` is the reduced copy a list's element type uses. */
function TypeFields({ value, onChange, regexErr, setRegexErr, errors, nested }: { value: Typed; onChange: (t: Typed) => void; regexErr: string | null; setRegexErr: (e: string | null) => void; errors: Record<string, string>; nested?: boolean }) {
  const { catalog } = useSchemaEnv();
  const set = (patch: Partial<Typed>) => onChange({ ...value, ...patch });
  const prefix = nested ? 'Element ' : '';
  const templateOptions = (kinds: string[]) =>
    catalog.templateList
      .filter((t) => kinds.includes(t.kind))
      .map((t) => ({ value: t.name, label: t.displayName ?? t.name, hint: `${t.name} · ${t.kind}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  const enums = useMemo(() => {
    const all = Array.from(catalog.enums.values()).sort((a, b) => a.name.localeCompare(b.name));
    return { own: all.filter((e) => !isSystemTemplate(e.name)), system: all.filter((e) => isSystemTemplate(e.name)) };
  }, [catalog.enums]);
  switch (value.type) {
    case 'string':
      return (
        <Field label={`${prefix}Regex`} error={regexErr ?? errors.regex} help="Checked when a value commits; compiled here on blur.">
          <TextInput
            value={value.regex}
            spellCheck={false}
            onChange={(e) => {
              set({ regex: e.target.value });
              if (regexErr) setRegexErr(null);
            }}
            onBlur={(e) => setRegexErr(regexError(e.target.value))}
            placeholder="^[A-Z_]+$"
          />
        </Field>
      );
    case 'localized_string':
      return (
        <Field label={`${prefix}Group`} help="The localization group the picker lists first.">
          <Select value={value.group ?? ''} onChange={(e) => set({ group: e.target.value || null })}>
            <option value="">Any group</option>
            {catalog.groups.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </Select>
        </Field>
      );
    case 'enum':
      return (
        <Field label={`${prefix}Enum`} required error={errors.enum}>
          <Select value={value.enum ?? ''} onChange={(e) => set({ enum: e.target.value || null })}>
            <option value="">Pick an enum…</option>
            {enums.own.length > 0 && (
              <optgroup label="Own enums">
                {enums.own.map((e) => (
                  <option key={e.name} value={e.name}>
                    {e.displayName ?? e.name} ({e.name})
                  </option>
                ))}
              </optgroup>
            )}
            {enums.system.length > 0 && (
              <optgroup label="System enums">
                {enums.system.map((e) => (
                  <option key={e.name} value={e.name}>
                    {e.displayName ?? e.name} ({e.name})
                  </option>
                ))}
              </optgroup>
            )}
          </Select>
        </Field>
      );
    case 'document':
      return (
        <Field label={`${prefix}Template`} error={errors.template} help="A component template makes the value an owned inline component.">
          <Combobox value={value.template} options={templateOptions(['document', 'singleton', 'component'])} onChange={(v) => set({ template: v })} emptyLabel="Any template" />
        </Field>
      );
    case 'object':
      return (
        <Field label={`${prefix}Template`} required error={errors.template} help="An inline template: its parameters are the object's fields.">
          <Combobox value={value.template} options={templateOptions(['inline'])} onChange={(v) => set({ template: v })} emptyLabel="None" />
        </Field>
      );
    case 'profile_data':
      return (
        <Field label={`${prefix}Template`} required error={errors.template}>
          <Combobox value={value.template} options={templateOptions(['profile'])} onChange={(v) => set({ template: v })} emptyLabel="None" />
        </Field>
      );
    case 'asset':
      return (
        <Field label={`${prefix}Asset kinds`} help="None picked: any kind.">
          <div className="flag-chips" role="group" aria-label={`${prefix}Asset kinds`}>
            {ASSET_KINDS.map((k) => {
              const on = value.assetKinds.includes(k);
              return (
                <button key={k} type="button" className={`chip flag-chip ${on ? 'chip-accent' : 'chip-outline'}`} aria-pressed={on} onClick={() => set({ assetKinds: on ? value.assetKinds.filter((x) => x !== k) : [...value.assetKinds, k] })}>
                  {k}
                </button>
              );
            })}
          </div>
        </Field>
      );
    default:
      return null;
  }
}

/** The link an inherited parameter's row and drawer show: it is edited on the base. */
export function BaseLink({ base }: { base: string }) {
  const { org, gameSlug, branch } = useSchemaEnv();
  return <Link to={routes.template(org, gameSlug, branch, base)}>{base}</Link>;
}
